/**
 * What the wizard leaves behind (#2490).
 *
 * Finishing used to be a toast and a redirect to the dashboard, so a workshop
 * learned nothing about what it had skipped — and the toast's own promise
 * ("you can change these in settings") pointed nowhere.
 *
 * The readiness rules already know exactly what is missing and which tab
 * fixes it, so this reuses them rather than counting steps. That difference
 * matters: a step can be marked done while its fields are empty, which is the
 * state that produced an unusable workshop in the first place.
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings } from '@/types';
import { useWorkshopReadiness } from '@/features/settings/useWorkshopReadiness';
import type { ReadinessIssue } from '@/features/settings/workshopReadiness';
import { byWorkflowOrder } from '@/features/settings/readinessOrder';

interface Props {
  settings: WorkshopSettings;
  /** Leaves onboarding for the dashboard. */
  onDone: () => void;
}

export function OnboardingSummary({ settings, onDone }: Props) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { issues, isLoading } = useWorkshopReadiness(settings);

  const sorted = [...issues].sort(byWorkflowOrder);
  const blocking = sorted.filter((i) => i.severity !== 'warning');
  const warnings = sorted.filter((i) => i.severity === 'warning');

  const goTo = (issue: ReadinessIssue) => {
    const params = new URLSearchParams({ tab: issue.target.tab });
    navigate(`/settings?${params.toString()}`);
  };

  const renderIssue = (issue: ReadinessIssue) => (
    <ListItemButton key={issue.key} onClick={() => goTo(issue)} data-testid={`gap-${issue.key}`}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        {issue.severity === 'warning' ? (
          <WarningAmberIcon color="warning" fontSize="small" />
        ) : (
          <ErrorOutlineIcon color="error" fontSize="small" />
        )}
      </ListItemIcon>
      <ListItemText primary={t(`summary.issue.${issue.key}`)} />
      <ChevronRightIcon fontSize="small" color="action" />
    </ListItemButton>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('summary.title')}
        </Typography>

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            {t('summary.checking')}
          </Typography>
        ) : sorted.length === 0 ? (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            {t('summary.allDone')}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('summary.description')}
            </Typography>

            {blocking.length > 0 && (
              <Box sx={{ mb: warnings.length > 0 ? 2 : 0 }}>
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={t('summary.blockingCount', { count: blocking.length })}
                  sx={{ mb: 1 }}
                />
                <List dense disablePadding>
                  {blocking.map(renderIssue)}
                </List>
              </Box>
            )}

            {warnings.length > 0 && (
              <Box>
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={t('summary.warningCount', { count: warnings.length })}
                  sx={{ mb: 1 }}
                />
                <List dense disablePadding>
                  {warnings.map(renderIssue)}
                </List>
              </Box>
            )}
          </>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button variant="contained" onClick={onDone} data-testid="summary-done">
            {t('summary.toDashboard')}
          </Button>
          {/*
            Settings is where every remaining gap is fixed, so it is offered
            even when nothing is missing — a workshop that wants to keep
            configuring should not have to find the way back.
          */}
          <Button
            variant="text"
            onClick={() => navigate('/settings')}
            data-testid="summary-settings"
          >
            {t('summary.toSettings')}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
